import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { customersAPI, portalLinksAPI } from "../../../../api/client";
import PermissionGate from "../../../../components/common/PermissionGate";
import { PERMISSIONS } from "../../../../constants/permissions";
import Toast from "../../components/Toast";
import ConfirmDialog from "../../../../components/common/ConfirmDialog";

/*
  Issues and manages the view-only links customers use to follow their own POs.

  The link itself is only ever available at the moment it is created or rotated —
  the server stores a hash, not the token — so the reveal panel is the one chance
  to copy it. That constraint drives the layout here.
*/

const VALIDITY_OPTIONS = [
  { value: 30, label: "30 days" },
  { value: 90, label: "90 days" },
  { value: 180, label: "6 months" },
  { value: 365, label: "1 year" },
];

const STATUS_STYLE = {
  active:  { bg: "#dcfce7", color: "#15803d", label: "Active" },
  expired: { bg: "#fff7ed", color: "#c2410c", label: "Expired" },
  revoked: { bg: "#fee2e2", color: "#dc2626", label: "Revoked" },
};

const cardStyle = {
  background: "#fff",
  border: "1px solid #e4ebf5",
  borderRadius: 12,
  padding: 20,
  marginBottom: 16,
};

const labelStyle = {
  display: "block",
  fontSize: 13,
  fontWeight: 600,
  color: "#374151",
  marginBottom: 6,
};

const inputStyle = {
  padding: "8px 12px",
  border: "1px solid #d6dfed",
  borderRadius: 8,
  fontSize: 13,
  boxSizing: "border-box",
  width: "100%",
};

function formatDate(value) {
  if (!value) return "—";
  return new Date(value).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

function formatDateTime(value) {
  if (!value) return "Never";
  return new Date(value).toLocaleString("en-GB", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

function portalUrl(token) {
  return `${window.location.origin}/portal/${token}`;
}

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.revoked;
  return (
    <span style={{
      background: s.bg, color: s.color, borderRadius: 999,
      padding: "3px 10px", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap",
    }}>
      {s.label}
    </span>
  );
}

// Shown once, immediately after create or rotate. There is no way back to it.
function RevealPanel({ reveal, onDismiss, onCopied }) {
  const url = portalUrl(reveal.token);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      onCopied("Link copied to clipboard");
    } catch {
      onCopied("Could not copy — select the link and copy manually");
    }
  };

  return (
    <div style={{ ...cardStyle, borderColor: "#86efac", background: "#f0fdf4" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h3 style={{ margin: "0 0 4px", color: "#15803d", fontSize: 15 }}>
            Link ready for {reveal.customerName}
          </h3>
          <p style={{ margin: 0, fontSize: 13, color: "#166534" }}>
            Copy it now — for security it is stored hashed and cannot be shown again.
            If it is lost, rotate the link to issue a fresh one.
          </p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          style={{ background: "none", border: "none", cursor: "pointer", color: "#166534", fontSize: 13 }}
        >
          Dismiss
        </button>
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 14, flexWrap: "wrap" }}>
        <input
          readOnly
          value={url}
          onFocus={(e) => e.target.select()}
          style={{ ...inputStyle, flex: "1 1 320px", fontFamily: "ui-monospace, Menlo, monospace", background: "#fff" }}
        />
        <button type="button" className="btn-primary" onClick={copy} style={{ whiteSpace: "nowrap" }}>
          Copy link
        </button>
      </div>
    </div>
  );
}

function InsightsPanel({ insights, onClose }) {
  return (
    <div style={{ ...cardStyle, borderColor: "#c7d7f0" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
        <h3 style={{ margin: 0, color: "#0f2d5c", fontSize: 15 }}>
          {insights.customerName} — link activity
        </h3>
        <button
          type="button"
          onClick={onClose}
          style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b", fontSize: 13 }}
        >
          Close
        </button>
      </div>

      <div style={{ display: "flex", gap: 24, marginTop: 14, flexWrap: "wrap" }}>
        {[
          { label: "Total views", value: insights.totalViews },
          { label: "Views (30 days)", value: insights.viewsLast30Days },
          { label: "Unique visitors (30 days)", value: insights.uniqueVisitorsLast30Days },
          { label: "Last opened", value: formatDateTime(insights.lastViewedAt) },
        ].map((stat) => (
          <div key={stat.label}>
            <div style={{ fontSize: 20, fontWeight: 700, color: "#0f2d5c" }}>{stat.value}</div>
            <div style={{ fontSize: 12, color: "#64748b" }}>{stat.label}</div>
          </div>
        ))}
      </div>

      {insights.recentViews.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6 }}>
            Recent opens
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 3, maxHeight: 160, overflowY: "auto" }}>
            {insights.recentViews.map((at, i) => (
              <div key={`${at}-${i}`} style={{ fontSize: 12, color: "#64748b" }}>{formatDateTime(at)}</div>
            ))}
          </div>
        </div>
      )}

      <p style={{ margin: "14px 0 0", fontSize: 12, color: "#94a3b8" }}>
        Visitor counts are approximate.
      </p>
    </div>
  );
}

export default function CustomerPortalLinks() {
  const [links, setLinks] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customerId, setCustomerId] = useState("");
  const [label, setLabel] = useState("");
  const [validityDays, setValidityDays] = useState(90);
  const [creating, setCreating] = useState(false);
  const [reveal, setReveal] = useState(null);
  const [insights, setInsights] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [rotateTarget, setRotateTarget] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [linkRes, custRes] = await Promise.all([
        portalLinksAPI.getAll(),
        customersAPI.getAll(),
      ]);
      setLinks(linkRes.data.data || []);
      setCustomers(custRes.data || []);
    } catch (err) {
      setToast({ msg: err.response?.data?.error || "Failed to load links", type: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!customerId) return;
    setCreating(true);
    try {
      const res = await portalLinksAPI.create({ customerId, label, validityDays });
      setReveal(res.data.data);
      setCustomerId("");
      setLabel("");
      await load();
    } catch (err) {
      setToast({ msg: err.response?.data?.error || "Failed to create link", type: "error" });
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async () => {
    try {
      await portalLinksAPI.revoke(revokeTarget.id);
      setToast({ msg: "Link revoked", type: "success" });
      await load();
    } catch (err) {
      setToast({ msg: err.response?.data?.error || "Failed to revoke link", type: "error" });
    } finally {
      setRevokeTarget(null);
    }
  };

  const handleRotate = async () => {
    try {
      const res = await portalLinksAPI.rotate(rotateTarget.id);
      setReveal(res.data.data);
      setToast({ msg: "New link issued — the old one no longer works", type: "success" });
      await load();
    } catch (err) {
      setToast({ msg: err.response?.data?.error || "Failed to rotate link", type: "error" });
    } finally {
      setRotateTarget(null);
    }
  };

  const openInsights = async (link) => {
    try {
      const res = await portalLinksAPI.insights(link.id);
      setInsights(res.data.data);
    } catch (err) {
      setToast({ msg: err.response?.data?.error || "Failed to load activity", type: "error" });
    }
  };

  return (
    <PermissionGate permission={PERMISSIONS.MANUFACTURING_PORTAL_MANAGE}>
      <div style={{ padding: 20, height: "100%", overflow: "auto", background: "#f5f8fc" }}>
        <Link className="back-link" to="/manufacturing/po-management">← Back to PO Management</Link>

        <div style={{ margin: "12px 0 20px" }}>
          <h1 style={{ margin: "0 0 4px", color: "#0f2d5c" }}>Customer Links</h1>
          <p style={{ margin: 0, color: "#64748b", fontSize: 14 }}>
            Give a customer a view-only link to follow their own purchase orders. No account needed,
            and they can only ever see their own orders.
          </p>
        </div>

        {reveal && (
          <RevealPanel
            reveal={reveal}
            onDismiss={() => setReveal(null)}
            onCopied={(msg) => setToast({ msg, type: "success" })}
          />
        )}

        {insights && <InsightsPanel insights={insights} onClose={() => setInsights(null)} />}

        <form onSubmit={handleCreate} style={cardStyle}>
          <h3 style={{ margin: "0 0 14px", color: "#0f2d5c", fontSize: 15 }}>Issue a new link</h3>
          <div style={{ display: "grid", gridTemplateColumns: "minmax(180px, 1fr) minmax(160px, 1fr) 140px auto", gap: 12, alignItems: "end" }}>
            <div>
              <label style={labelStyle} htmlFor="cpl-customer">Customer *</label>
              <select
                id="cpl-customer"
                style={inputStyle}
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
              >
                <option value="">Select a customer…</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}{c.poCount ? ` (${c.poCount} POs)` : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle} htmlFor="cpl-label">Note (optional)</label>
              <input
                id="cpl-label"
                style={inputStyle}
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. sent to purchasing team"
              />
            </div>
            <div>
              <label style={labelStyle} htmlFor="cpl-validity">Valid for</label>
              <select
                id="cpl-validity"
                style={inputStyle}
                value={validityDays}
                onChange={(e) => setValidityDays(Number(e.target.value))}
              >
                {VALIDITY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <button type="submit" className="btn-primary" disabled={!customerId || creating} style={{ whiteSpace: "nowrap" }}>
              {creating ? "Creating…" : "Create link"}
            </button>
          </div>
        </form>

        <div style={{ ...cardStyle, padding: 0, overflow: "hidden" }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Loading…</div>
          ) : links.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>
              No customer links yet. Issue one above.
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    {["Customer", "Note", "Status", "Expires", "Views", "Last opened", ""].map((h, i) => (
                      <th key={h || i} style={{ padding: "10px 16px", textAlign: "left", fontWeight: 700, color: "#374151", borderBottom: "1px solid #e4ebf5", whiteSpace: "nowrap" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {links.map((link) => (
                    <tr key={link.id} style={{ borderBottom: "1px solid #f1f5f9", opacity: link.status === "active" ? 1 : 0.6 }}>
                      <td style={{ padding: "10px 16px", color: "#0f2d5c", fontWeight: 600 }}>{link.customerName}</td>
                      <td style={{ padding: "10px 16px", color: "#64748b" }}>{link.label || "—"}</td>
                      <td style={{ padding: "10px 16px" }}><StatusPill status={link.status} /></td>
                      <td style={{ padding: "10px 16px", color: "#64748b", whiteSpace: "nowrap" }}>{formatDate(link.expiresAt)}</td>
                      <td style={{ padding: "10px 16px", color: "#0f2d5c" }}>{link.viewCount}</td>
                      <td style={{ padding: "10px 16px", color: "#64748b", whiteSpace: "nowrap" }}>{formatDateTime(link.lastViewedAt)}</td>
                      <td style={{ padding: "10px 16px", textAlign: "right", whiteSpace: "nowrap" }}>
                        <button
                          type="button"
                          onClick={() => openInsights(link)}
                          style={{ background: "none", border: "1px solid #d6dfed", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 12, color: "#475569", marginRight: 6 }}
                        >
                          Activity
                        </button>
                        <button
                          type="button"
                          onClick={() => setRotateTarget(link)}
                          style={{ background: "none", border: "1px solid #c7d7f0", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 12, color: "#1d4ed8", marginRight: 6 }}
                        >
                          Rotate
                        </button>
                        {link.status !== "revoked" && (
                          <button
                            type="button"
                            onClick={() => setRevokeTarget(link)}
                            style={{ background: "none", border: "1px solid #fca5a5", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 12, color: "#dc2626" }}
                          >
                            Revoke
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {revokeTarget && (
          <ConfirmDialog
            title="Revoke this link?"
            message={`${revokeTarget.customerName} will immediately lose access. Anyone still holding the link will see a "link revoked" message.`}
            confirmLabel="Revoke"
            confirmClass="btn-danger"
            onConfirm={handleRevoke}
            onClose={() => setRevokeTarget(null)}
          />
        )}

        {rotateTarget && (
          <ConfirmDialog
            title="Issue a replacement link?"
            message={`A new link will be generated for ${rotateTarget.customerName} and the current one will stop working immediately. Use this if the old link may have been forwarded to the wrong people.`}
            confirmLabel="Rotate"
            onConfirm={handleRotate}
            onClose={() => setRotateTarget(null)}
          />
        )}

        {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
      </div>
    </PermissionGate>
  );
}
